import { Controller } from "@hotwired/stimulus";

const STACKED_BREAKPOINT = 600;
const STACKED_END_HEIGHT = 300;

export default class extends Controller {
  static targets = [
    "dragStart",
    "dragEnd",
    "startPanel",
    "endPanel",
    "mainPanel",
  ];

  static values = {
    // START PANEL
    startSnapable: Boolean,
    startMinSize: Number,
    startSnappedSize: Number,
    startSize: String,
    startHidden: Boolean,

    // END PANEL
    endSnapable: Boolean,
    endMinSize: Number,
    endClosedSize: Number,
    endSize: String,
    endHidden: Boolean,

    // GLOBAL SNAP CONFIG
    snapCloseFinal: Number,
    snapOpenThreshold: Number,

    // ORIENTATION
    orientation: String, // "horizontal" (default) or "vertical"

    // localStorage namespace for size persistence (FOUC-prevented in the
    // inline script in panels_component.html.erb).
    storageKey: String,
  };

  _persistSize(side, size) {
    if (!this.storageKeyValue) return;
    try {
      localStorage.setItem(`${this.storageKeyValue}:${side}_size`, size);
    } catch (e) {}
  }

  _persistUserDragSize(side, size) {
    if (!this.storageKeyValue) return;
    try {
      localStorage.setItem(
        `${this.storageKeyValue}:${side}_user_drag_size`,
        size,
      );
    } catch (e) {}
  }

  _persistSnap(side, snapped) {
    if (!this.storageKeyValue) return;
    try {
      if (snapped) {
        localStorage.setItem(`${this.storageKeyValue}:${side}_snapped`, "true");
      } else {
        localStorage.removeItem(`${this.storageKeyValue}:${side}_snapped`);
      }
    } catch (e) {}
  }

  // Helper: check orientation
  get isVertical() {
    return this.orientationValue === "vertical";
  }

  connect() {
    const fromStorage = (side) => {
      if (!this.storageKeyValue) return null;
      try {
        const v = localStorage.getItem(`${this.storageKeyValue}:${side}_size`);
        const n = v ? parseInt(v, 10) : NaN;
        return Number.isFinite(n) ? n : null;
      } catch {
        return null;
      }
    };
    this.startSize = fromStorage("start") || parseInt(this.startSizeValue, 10) || 350;
    this.endSize = fromStorage("end") || parseInt(this.endSizeValue, 10) || 350;
    this.proposedStartSize = this.startSize;
    this.proposedEndSize = this.endSize;
    this.boundUpdateSize = this.updateSize.bind(this);
    this.startHidden = this.startHiddenValue;
    this.endHidden = this.endHiddenValue;

    // Set initial CSS properties based on hidden state
    if (this.startHidden) {
      this.element.style.setProperty("--start-panel-size", "0px");
    }
    if (this.endHidden) {
      this.element.style.setProperty("--end-panel-size", "0px");
    }

    // Stacked mode only applies to horizontal layouts. A vertical component
    // (the inner query/vis split) is already stacked.
    this.stacked = false;
    if (!this.isVertical) {
      // Capture the horizontal grid template set by the inline FOUC script
      // so we can restore exactly what was rendered when leaving stacked
      // mode — regardless of which slots (start / main / end) are present.
      this.originalGridAreas = this.element.style.gridTemplateAreas;
      this.originalGridColumns = this.element.style.gridTemplateColumns;

      this.stackedObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? this.element.offsetWidth;
        if (width < STACKED_BREAKPOINT && !this.stacked) {
          this.enterStackedMode();
        } else if (width >= STACKED_BREAKPOINT && this.stacked) {
          this.exitStackedMode();
        }
      });
      this.stackedObserver.observe(this.element);
    }
  }

  disconnect() {
    this.stackedObserver?.disconnect();
  }

  // Build a stacked grid template from whichever panel slots this component
  // actually rendered. Order: start | start-resizer | main | end-resizer | end.
  buildStackedTemplate() {
    const areas = [];
    const rows = [];
    if (this.hasStartPanelTarget) {
      areas.push('"start-panel"');
      rows.push("var(--start-panel-size)");
      areas.push('"start-resizer"');
      rows.push("var(--start-resizer-size)");
    }
    areas.push('"main-panel"');
    rows.push("1fr");
    if (this.hasEndPanelTarget) {
      areas.push('"end-resizer"');
      rows.push("var(--end-resizer-size)");
      areas.push('"end-panel"');
      rows.push("var(--end-panel-size)");
    }
    return { areas: areas.join(" "), rows: rows.join(" ") };
  }

  enterStackedMode() {
    this.stacked = true;
    this.savedOrientation = this.orientationValue;

    // The size we restore on exit must represent a valid desktop OPEN width.
    // If the panel is currently snapped, this.endSize is the rail width — not
    // useful — so fall back to the configured default. Otherwise keep the
    // current width (covers user-dragged sizes).
    const configuredOpen = parseInt(this.endSizeValue, 10) || this.endSize;
    const snappedOnEntry = this.element.classList.contains("end-panel-snapped");
    this.savedEndSize = snappedOnEntry ? configuredOpen : this.endSize;

    // Switch orientation so drag handlers + CSS use vertical coordinates.
    this.orientationValue = "vertical";
    this.element.classList.add("panels-stacked");

    const { areas, rows } = this.buildStackedTemplate();
    this.element.style.gridTemplateColumns = "";
    this.element.style.gridTemplateAreas = areas;
    this.element.style.gridTemplateRows = rows;

    if (!this.hasEndPanelTarget) return;

    // Pick the right end-panel size for the current state:
    //   - hidden (e.g. dashboard config panel before opening) → 0
    //   - snapped (tab rail collapsed) → rail width
    //   - open → STACKED_END_HEIGHT
    const hidden = this.element.classList.contains("end-panel-hidden");
    const snapped = this.element.classList.contains("end-panel-snapped");
    const newSize = hidden ? 0 : snapped ? this.endClosedSizeValue : STACKED_END_HEIGHT;
    this.endSize = STACKED_END_HEIGHT; // remembered "open" size
    this.proposedEndSize = newSize;
    this.element.style.setProperty("--end-panel-size", `${newSize}px`);
  }

  exitStackedMode() {
    this.stacked = false;
    this.orientationValue = this.savedOrientation || "horizontal";
    this.element.classList.remove("panels-stacked");

    // Restore the original horizontal grid template captured at connect.
    this.element.style.gridTemplateRows = "";
    this.element.style.gridTemplateAreas = this.originalGridAreas;
    this.element.style.gridTemplateColumns = this.originalGridColumns;

    if (!this.hasEndPanelTarget) return;

    // Preserve the current visible state across the breakpoint:
    //   - hidden → 0 (the .end-panel-hidden class CSS handles display)
    //   - snapped → rail width (so the thin strip stays thin on desktop)
    //   - open → the size from before we entered stacked
    const hidden = this.element.classList.contains("end-panel-hidden");
    const snapped = this.element.classList.contains("end-panel-snapped");
    const restored = hidden ? 0 : snapped ? this.endClosedSizeValue : this.savedEndSize;
    this.endSize = this.savedEndSize;
    this.proposedEndSize = restored;
    this.element.style.setProperty("--end-panel-size", `${restored}px`);
  }

  resetStartAutoSize(event) {
    if (!this.element.dataset.startUserDragSize && !this.element.dataset.userDragSize) return;
    event.preventDefault();
    delete this.element.dataset.startUserDragSize;
    delete this.element.dataset.userDragSize;
    this.element.style.removeProperty("--start-panel-size");
    if (this.storageKeyValue) {
      try {
        localStorage.removeItem(`${this.storageKeyValue}:start_size`);
        localStorage.removeItem(`${this.storageKeyValue}:start_user_drag_size`);
      } catch (e) {}
    }
    // Consumers (e.g. QueryComponent) listen for this on the panels
    // element to invalidate their content-size cache and recompute.
    this.element.dispatchEvent(
      new CustomEvent("panels:autoSize", { bubbles: true, detail: { side: "start" } }),
    );
  }

  // Helper getters
  isSnapable(side) {
    return side === "start" ? this.startSnapableValue : this.endSnapableValue;
  }
  getMinSize(side) {
    return side === "start" ? this.startMinSizeValue : this.endMinSizeValue;
  }
  getClosedSize(side) {
    return side === "start"
      ? this.startSnappedSizeValue
      : this.endClosedSizeValue;
  }
  getPanelSize(side) {
    return side === "start" ? this.startSizeValue : this.endSizeValue;
  }

  // --- SNAP TOGGLE ---
  toggleStartPanel() {
    if (this.isSnapable("start")) {
      const snapped = this.element.classList.toggle("start-panel-snapped");
      const size = snapped ? this.startSnappedSizeValue : this.startSize;
      this.element.style.setProperty("--start-panel-size", `${size}px`);
      this._persistSnap("start", snapped);
      this._persistSize("start", size);
    } else {
      // For non-snapable panels, hide/show completely
      this.startHidden = !this.startHidden;
      if (this.startHidden) {
        this.element.style.setProperty("--start-panel-size", "0px");
        this.element.classList.add("start-panel-hidden");
      } else {
        this.element.style.setProperty(
          "--start-panel-size",
          this.startSizeValue,
        );
        this.element.classList.remove("start-panel-hidden");
      }
    }
  }

  // Snap-close preserves `endSize` so reopen can restore the last open width.
  applyEndState({ size, snapped }) {
    this.element.classList.toggle("end-panel-snapped", snapped);
    if (!snapped) {
      this.endSize = size;
      this.proposedEndSize = size;
    }
    this.element.style.setProperty("--end-panel-size", `${size}px`);
    this._persistSnap("end", snapped);
    this._persistSize("end", size);
  }

  toggleEndPanel() {
    if (this.isSnapable("end")) {
      const snapped = this.element.classList.toggle("end-panel-snapped");
      const size = snapped ? this.endClosedSizeValue : this.endSize;
      this.element.style.setProperty("--end-panel-size", `${size}px`);
      this._persistSnap("end", snapped);
      this._persistSize("end", size);
      this.element.dispatchEvent(
        new CustomEvent("panels:toggle", {
          bubbles: true,
          detail: { side: "end", snapped },
        }),
      );
    } else {
      // For non-snapable panels, hide/show completely
      this.endHidden = !this.endHidden;
      if (this.endHidden) {
        this.element.style.setProperty("--end-panel-size", "0px");
        this.element.classList.add("end-panel-hidden");
      } else {
        this.element.style.setProperty("--end-panel-size", this.endSizeValue);
        this.element.classList.remove("end-panel-hidden");
      }
    }
  }

  showEndPanel() {
    if (this.endHidden) {
      this.endHidden = false;
      this.element.style.setProperty("--end-panel-size", this.endSizeValue);
      this.element.classList.remove("end-panel-hidden");
    }
  }

  hideEndPanel() {
    if (!this.endHidden) {
      this.endHidden = true;
      this.element.style.setProperty("--end-panel-size", "0px");
      this.element.classList.add("end-panel-hidden");
    }
  }

  _dispatchDrag(phase) {
    this.element.dispatchEvent(
      new CustomEvent(`panels:${phase}`, {
        bubbles: true,
        detail: { orientation: this.orientationValue || "horizontal" },
      }),
    );
  }

  updateSize() {
    if (this.proposedStartSize !== this.startSize) {
      this.element.style.setProperty(
        "--start-panel-size",
        `${this.proposedStartSize}px`,
      );
      this.startSize = this.proposedStartSize;
    }
    if (this.proposedEndSize !== this.endSize) {
      this.element.style.setProperty(
        "--end-panel-size",
        `${this.proposedEndSize}px`,
      );
      this.endSize = this.proposedEndSize;
    }
  }

  // --- START PANEL DRAG ---
  startStart(event) {
    event.preventDefault();
    const snapable = this.isSnapable("start");
    // CHANGED: orientation-aware pointer and size
    let initialPos = this.isVertical ? event.clientY : event.clientX;
    let initialStartSize = parseInt(
      getComputedStyle(this.startPanelTarget)[
        this.isVertical ? "height" : "width"
      ],
      10,
    );
    this.dragStartTarget.classList.add("dragging");
    const startedAt = Date.now();

    this.element.dataset.dragging = "true";

    let dragStarted = false;
    const stopDrag = () => {
      const wasDragging = dragStarted;
      if (dragStarted) {
        this._dispatchDrag("dragend");
        dragStarted = false;
      }
      this.dragStartTarget.classList.remove("dragging");
      document.removeEventListener("mousemove", dragStart);
      document.removeEventListener("mouseup", stopDrag);
      delete this.element.dataset.dragging;
      if (!wasDragging && Date.now() - startedAt < 200) {
        if (snapable) {
          this.toggleStartPanel();
        } else {
          this.resetStartAutoSize(event);
        }
      } else {
        this._persistSize("start", this.proposedStartSize);
        this._persistUserDragSize("start", this.proposedStartSize);
      }
    };

    const dragStart = (event) => {
      if (!dragStarted) {
        this._dispatchDrag("dragstart");
        dragStarted = true;
      }
      // CHANGED: orientation-aware pointer
      let pointer = this.isVertical ? event.clientY : event.clientX;
      let newSize = initialStartSize + (pointer - initialPos);
      this.element.dataset.userDragged = "true";

      if (snapable) {
        // Snap closed
        if (
          initialStartSize >= this.snapCloseFinalValue &&
          newSize < this.snapCloseFinalValue
        ) {
          this.element.classList.add("start-panel-snapped");
          this.element.style.setProperty(
            "--start-panel-size",
            `${this.startSnappedSizeValue}px`,
          );
          this.proposedStartSize = this.startSnappedSizeValue;
          stopDrag();
          return;
        }
        // Snap open
        if (
          this.element.classList.contains("start-panel-snapped") &&
          newSize > this.snapOpenThresholdValue
        ) {
          this.element.classList.remove("start-panel-snapped");
          this.element.style.setProperty(
            "--start-panel-size",
            `${this.startSize}px`,
          );
          this.proposedStartSize = this.startSize;
          stopDrag();
          return;
        }
      } else {
        // Not snapable: clamp to minWidth
        if (newSize < this.startMinSizeValue) newSize = this.startMinSizeValue;
      }

      // Record the clamped size so auto-grow logic that uses this value
      // (e.g. the query textarea's resizeQueryBox) sees the actual panel
      // size, not the raw pointer delta.
      this.element.dataset.startUserDragSize = newSize;
      this.element.dataset.userDragSize = newSize;
      this.proposedStartSize = newSize;
      requestAnimationFrame(this.boundUpdateSize);
    };

    document.addEventListener("mousemove", dragStart);
    document.addEventListener("mouseup", stopDrag);
  }

  // --- END PANEL DRAG ---
  startEnd(event) {
    event.preventDefault();
    const snapable = this.isSnapable("end");
    // CHANGED: orientation-aware pointer and size
    let initialPos = this.isVertical ? event.clientY : event.clientX;
    let initialEndSize = parseInt(
      getComputedStyle(this.endPanelTarget)[
        this.isVertical ? "height" : "width"
      ],
      10,
    );
    this.dragEndTarget.classList.add("dragging");
    const startedAt = Date.now();

    let dragStarted = false;
    const stopDrag = () => {
      const wasDragging = dragStarted;
      if (dragStarted) {
        this._dispatchDrag("dragend");
        dragStarted = false;
      }
      this.dragEndTarget.classList.remove("dragging");
      document.removeEventListener("mousemove", dragEnd);
      document.removeEventListener("mouseup", stopDrag);
      if (snapable && !wasDragging && Date.now() - startedAt < 200) {
        this.toggleEndPanel();
      } else {
        this._persistSize("end", this.proposedEndSize);
        this._persistUserDragSize("end", this.proposedEndSize);
      }
    };

    const dragEnd = (event) => {
      if (!dragStarted) {
        this._dispatchDrag("dragstart");
        dragStarted = true;
      }
      // CHANGED: orientation-aware pointer
      let pointer = this.isVertical ? event.clientY : event.clientX;
      let newSize = initialEndSize + (initialPos - pointer);

      if (snapable) {
        // Snap closed
        if (
          initialEndSize >= this.snapCloseFinalValue &&
          newSize < this.snapCloseFinalValue
        ) {
          this.element.classList.add("end-panel-snapped");
          this.element.style.setProperty(
            "--end-panel-size",
            `${this.endClosedSizeValue}px`,
          );
          this.proposedEndSize = this.endClosedSizeValue;
          this._persistSnap("end", true);
          this.element.dispatchEvent(
            new CustomEvent("panels:toggle", {
              bubbles: true,
              detail: { side: "end", snapped: true },
            }),
          );
          stopDrag();
          return;
        }
        // Snap open
        if (
          this.element.classList.contains("end-panel-snapped") &&
          newSize > this.snapOpenThresholdValue
        ) {
          this.element.classList.remove("end-panel-snapped");
          this.element.style.setProperty(
            "--end-panel-size",
            `${this.endSize}px`,
          );
          this.proposedEndSize = this.endSize;
          this._persistSnap("end", false);
          this.element.dispatchEvent(
            new CustomEvent("panels:toggle", {
              bubbles: true,
              detail: { side: "end", snapped: false },
            }),
          );
          stopDrag();
          return;
        }
      } else {
        // Not snapable: clamp to minWidth
        if (newSize < this.endMinSizeValue) newSize = this.endMinSizeValue;
      }

      this.element.dataset.endUserDragSize = newSize;
      this.element.dataset.userDragSize = newSize;
      this.proposedEndSize = newSize;
      requestAnimationFrame(this.boundUpdateSize);
    };

    document.addEventListener("mousemove", dragEnd);
    document.addEventListener("mouseup", stopDrag);
  }
}
